import React, { useCallback, useEffect, useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePlaidLink } from "react-plaid-link";
import { useDispatch } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";

import Text from "components/common/Text";
import Button from "components/common/Button";
import RadioButton from "components/common/RadioButtons";
import CustomTooltip from "components/common/Tooltip";
import AddPaymentLoaderModal from "components/Screens/CustomerOnboarding/AddPaymentLoaderModal";
import OnboardingBottomBar from "components/Screens/CustomerOnboarding/AddPaymentMethod/OnboardingBottomBar";
import useDeviceInfo from "components/Hooks/useDeviceInfo";
import { AlertType } from "components/common/Snackbar";

import AddAchPaymentSchemaNew from "validations/Onboarding/AddAchPaymentSchemaNew";
import { customerOnboardingAddPayment, getLinkToken } from "services/customer";

import { showSnackbar } from "store/actions/Utility";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertLinkIcon from "@mui/icons-material/InsertLink";

import { CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE } from "constants/routes";

import "./AddPaymentMethod.scss";

function AddAchPayment({
  handleBackToPaymentMethods,
  loading,
  setLoading,
  navigate,
  isStepSkippable,
  onSkip,
  setIosPadding,
  customerId,
}) {
  const [linkToken, setLinkToken] = useState(null);

  const dispatch = useDispatch();
  const { isMobile } = useDeviceInfo();
  const { stripeFc = false } = useFlags();

  const handleCustomerOnboardingAddPayment = useCallback(
    async (data) => {
      try {
        setLoading(true);
        const response = await customerOnboardingAddPayment(data, customerId);
        if (response?.success) {
          dispatch(
            showSnackbar({
              dispatch,
              type: AlertType.success,
              message: "Payment method added successfully!",
            })
          );
          const { chargeInitiated = true, scheduledAt = null } = response ?? {};
          setTimeout(() => {
            navigate(CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE, {
              state: {
                chargeInitiated,
                scheduledAt,
              },
            });
          }, 2000);
        }
      } catch (error) {
        const errorMessage = error[0]?.description || "Something went wrong, please try again";
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: errorMessage,
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [customerId, setLoading, dispatch, navigate]
  );

  // Plaid integration (only when stripeFc is OFF)
  const onSuccess = useCallback(
    (publicToken) => {
      const setToken = async () => {
        await handleCustomerOnboardingAddPayment({ publicToken });
      };
      setToken();
    },
    [handleCustomerOnboardingAddPayment]
  );

  const config = {
    token: linkToken?.link_token,
    onSuccess,
    onExit: (err) => {
      if (err) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: err.error_message,
          })
        );
      }
    },
  };

  const { open } = usePlaidLink(config);

  const methods = useForm({
    resolver: yupResolver(AddAchPaymentSchemaNew),
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = methods;

  const onSubmit = async (data = {}) => {
    await handleCustomerOnboardingAddPayment(data);
  };

  const handleFieldChange = (value, field) => {
    field.onChange(value);
    if (setIosPadding) {
      setIosPadding(true);
    }
  };

  useEffect(() => {
    const initializePaymentConnection = async () => {
      if (!stripeFc) {
        try {
          const token = await getLinkToken();
          setLinkToken(token);
        } catch (error) {
          dispatch(
            showSnackbar({
              dispatch,
              type: AlertType.error,
              message: "Failed to get link token",
            })
          );
        }
      }
    };
    initializePaymentConnection();
  }, [dispatch, stripeFc]);

  useEffect(() => {
    if (isMobile && /iPad|iPhone|iPod/.test(navigator.userAgent) && setIosPadding) {
      setIosPadding(true);
    }
  }, [isMobile, setIosPadding]);

  return (
    <div className="onboarding-add-ach-payment-container">
      <div className="onboarding-add-ach-payment-main-div">
        <div className="ach-header">
          <ArrowBackIcon className="back-button-icon" onClick={handleBackToPaymentMethods} />
          <div className="heading-h3-semibold color-text-heading-primary">
            {stripeFc ? "Enter your bank account details" : "Add your ACH payment details"}
          </div>
        </div>
        {!stripeFc && (
          <div className="para-body-m-regular color-text-body-secondary mt-1">
            Provide your ACH information to set up your payment method
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="ach-payment-form">
            <div className="onboarding-input-group">
              <p className="para-body-m-medium text-headings-secondary">Account holder type</p>
              <div className="flex gap-1 flex-nowrap">
                <Controller
                  name="accountHolderType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RadioButton
                      label="Company"
                      value="company"
                      withContainer
                      checked={field.value === "company"}
                      onChange={() => handleFieldChange("company", field)}
                    />
                  )}
                />
                <Controller
                  name="accountHolderType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RadioButton
                      withContainer
                      label="Individual"
                      value="individual"
                      checked={field.value === "individual"}
                      onChange={() => handleFieldChange("individual", field)}
                    />
                  )}
                />
              </div>
            </div>

            <div className="onboarding-input-group">
              <p className="para-body-m-medium text-headings-secondary mt-15">Account type</p>
              <div className="flex gap-1 flex-nowrap">
                <Controller
                  name="accountType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RadioButton
                      withContainer
                      label="Checking"
                      value="Checking"
                      checked={field.value === "checking"}
                      onChange={() => handleFieldChange("checking", field)}
                    />
                  )}
                />
                <Controller
                  name="accountType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RadioButton
                      withContainer
                      label="Savings"
                      value="Savings"
                      checked={field.value === "savings"}
                      onChange={() => handleFieldChange("savings", field)}
                    />
                  )}
                />
              </div>
            </div>

            <div className="mt-15">
              <Text label="Account holder name" name="accountHolderName" isPayment />
            </div>

            <div className="input-group-add-payment">
              <Text
                label="Account number"
                labelIcon={
                  <CustomTooltip content="Your account number is the unique identifier assigned to your bank account.">
                    <InfoOutlinedIcon className="onboarding-add-payment-label-icon" />
                  </CustomTooltip>
                }
                name="accountNumber"
                isPayment
              />
              <Text
                label="ACH routing number"
                labelIcon={
                  <CustomTooltip content="Your ACH routing number is a 9-digit code that identifies your bank and branch.">
                    <InfoOutlinedIcon className="onboarding-add-payment-label-icon" />
                  </CustomTooltip>
                }
                isPayment
                name="routingNumber"
              />
            </div>

            {!stripeFc && (
              <>
                <div>
                  <Button
                    type="button"
                    onClick={() => open()}
                    name="connectViaPlaid"
                    variant="text"
                    size="medium"
                    startIcon={<InsertLinkIcon />}
                    label="Or Connect Your Bank via Plaid"
                    isFullWidth={false}
                  />
                </div>

                <div className="add-ach-note-wrapper">
                  <p className="para-body-s-regular mt-2 pt-025 pb-125 color-text-body-primary">
                    Note: <br />
                    Only the current month&apos;s bill will be charged. <br />
                    If payment is not made, the start date for your employee(s) may be delayed.
                  </p>
                </div>
              </>
            )}
          </form>
        </FormProvider>
      </div>

      <OnboardingBottomBar
        isStepSkippable={isStepSkippable}
        onSkip={onSkip}
        primaryLabel="Confirm payment details"
        onPrimary={handleSubmit(onSubmit)}
        isPrimaryDisabled={!isValid}
        isLoading={loading}
      />
      {loading && (
        <AddPaymentLoaderModal
          title="Hang tight, We're processing your payment..."
          description="Please give us a moment to finalize your payment details. This may take a few seconds."
          open={loading}
        />
      )}
    </div>
  );
}

export default AddAchPayment;
